<!-- eslint-disable vue-a11y/click-events-have-key-events -->
<template>
  <div
    ref="mainElement"
    class="sam-search"
    :class="mainClassObj"
    data-samora
    @focus.capture="toggleFocus(true)"
    @blur.capture="toggleFocus(false)"
  >
    <input
      ref="input"
      v-model="model"
      type="text"
      :name="name"
      :disabled="disabled"
      :aria-label="''"
      :placeholder="placeholder"
      class="sam-search__input"
      v-bind="$attrs"
      @change="handleChange"
      @input="handleInput"
    />
    <div class="sam-search__icons d-flex items-center">
      <SamIcon
        v-if="!disabled && model.length"
        v-bind="clearIconProps"
        data-testid="sam-search__clear"
        @click.stop.capture="clear"
      />
      <SamIcon
        v-bind="searchIconProps"
        data-testid="sam-search__search"
        @click.stop.capture="search"
      />
    </div>
  </div>
</template>

<script>
import SAMORA_TYPES from '../../../utils/constants/samora-types.enum';
import SamIcon, { ICON_SIZES } from '../SamIcon/SamIcon.vue';
import { debounce } from '../../../utils/utils';

const { SEARCH: SAMORA_SEARCH_TYPES } = SAMORA_TYPES;

export const SEARCH_TYPES = SAMORA_SEARCH_TYPES;

const EVENTS = {
  SEARCH: 'search',
  CLEAR: 'clear',
  INPUT: 'input',
  CHANGE: 'change',
  FOCUS: 'focus',
  BLUR: 'blur',
};

export default {
  name: 'SamSearch',
  components: {
    SamIcon,
  },
  inheritAttrs: false,
  model: {
    prop: 'data',
  },
  props: {
    /**
     * The SearchInput name (html attribute name)
     */
    name: {
      type: String,
      required: false,
      default: '',
    },
    /**
     * The SearchInput data variable
     */
    data: {
      type: [Number, String],
      required: false,
      default: undefined,
    },
    /**
     * If the SearchInput is disabled (html attribute disabled)
     * @values true, false
     */
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * SearchInput placeholder
     */
    placeholder: {
      type: String,
      required: false,
      default: '',
    },
    /**
     * Boolean to determine if search is triggered on input change or when search icon clicked.
     * If true, 'search' event is emitted after a certain debounce time
     */
    immediate: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * This value is used to specify debounce time used when search is set to search on input change
     */
    debounceTime: {
      type: Number,
      required: false,
      default: 0,
    },
    /**
     * The search input Samora type
     * @values input, filter
     */
    samType: {
      type: String,
      required: false,
      default: SEARCH_TYPES.INPUT,
      validator: (val) => Object.values(SEARCH_TYPES).includes(val),
    },
  },
  data() {
    return {
      model: '',
      isFocused: false,
      debouncedSearch: undefined,
    };
  },
  computed: {
    clearIconProps() {
      return {
        icon: 'interface-close',
        class: 'sam-search__icon--interactive',
        size: this.samType === SEARCH_TYPES.INPUT ? ICON_SIZES.SMALL : ICON_SIZES.MEDIUM,
      };
    },
    searchIconProps() {
      return {
        icon: 'interface-search',
        class: this.immediate ? '' : 'sam-search__icon--interactive',
        size: this.samType === SEARCH_TYPES.INPUT ? ICON_SIZES.SMALL : ICON_SIZES.MEDIUM,
      };
    },
    mainClassObj() {
      return [
        `is-${this.samType}`,
        {
          'is-focus': this.isFocused,
          'is-disabled': this.disabled,
        },
      ];
    },
  },
  watch: {
    data: {
      handler(newVal) {
        this.model = newVal !== undefined && newVal !== null ? `${newVal}` : '';
      },
      immediate: true,
    },
  },
  created() {
    this.debouncedSearch = debounce(this.search, this.debounceTime);
  },
  methods: {
    toggleFocus(focus, forceFocus = false) {
      this.isFocused = focus;

      if (focus) {
        /**
         * Triggers when component focus changes (@focus)
         * @event focus
         */
        this.$emit(EVENTS.FOCUS);
      } else {
        /**
         * Triggers when component focus changes (@blur)
         * @event blur
         */
        this.$emit(EVENTS.BLUR);
      }

      if (focus && forceFocus) {
        // eslint-disable-next-line no-unused-expressions
        this.$refs.input?.focus?.();
      }
    },
    clear() {
      if (this.disabled) return;

      /**
       * Triggers when clear button is clicked (@clear)
       * @event clear
       */
      this.$emit(EVENTS.CLEAR);

      // force focus always (better UX)
      // eslint-disable-next-line no-unused-expressions
      this.$refs.input?.focus?.();
    },
    search(e) {
      if (this.disabled || !this.model.length || (e && this.immediate)) return;

      /**
       * Triggers when search is requested (@search)
       * @event search
       * @property {string} model value set to search
       */
      this.$emit(EVENTS.SEARCH, this.model);

      // force focus always (better UX)
      // eslint-disable-next-line no-unused-expressions
      this.$refs.input?.focus?.();
    },
    handleInput() {
      if (this.immediate) {
        this.debouncedSearch();
      }

      /**
       * Triggers when input changes it's value during value input (@input)
       * @event input
       * @property {number, string} model new value set
       */
      this.$emit(EVENTS.INPUT, this.model);
    },
    handleChange() {
      if (this.immediate) {
        this.debouncedSearch();
      }

      /**
       * Triggers when the input changes it's value (@change)
       * @event change
       * @property {number, string} model new value set
       */
      this.$emit(EVENTS.CHANGE, this.model);
    },
  },
};
</script>

<style lang="scss" scoped>
.sam-search {
  width: 100%;
  border: $size--0--fourth solid $neutral--60;
  border-radius: $size--9;
  height: $size--9;
  position: relative;
  transition: all $default-time $default-easing;
  color: $neutral--90;

  &::-moz-focus-inner {
    border: 0;
  }

  &:hover:not(.is-disabled) {
    background-color: $informative--surface;
    border-color: $informative--hover;
    color: $informative--hover;

    ::-webkit-input-placeholder,
    ::-moz-placeholder,
    :-ms-input-placeholder,
    ::-webkit-input-placeholder,
    ::placeholder {
      color: $informative--hover;
    }
  }

  &.is-focus {
    border-color: $accent--unbabel-blue;
    outline: 3px solid $informative--border;

    .sam-search__input:-webkit-autofill,
    .sam-search__input:-webkit-autofill:hover,
    .sam-search__input:-webkit-autofill:focus,
    .sam-search__input:-webkit-autofill:active,
    .sam-search__input:-webkit-autofill:first-line {
      border: 1px solid $accent--unbabel-blue;
    }
  }

  &__icons {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    right: $size--4;
    transition: all $default-time $default-easing;

    & > * {
      &:not(:last-of-type) {
        margin-right: $size--2;
      }
    }
  }

  &__icon {
    &--interactive {
      cursor: pointer;
    }
  }

  &__input {
    @extend .text--body-2--medium, .px-4, .py-3;
    width: 100%;
    height: 100%;
    outline: none;
    border: none;
    background-color: transparent;
    box-sizing: border-box;
    cursor: pointer;
    position: relative;

    &::placeholder {
      @extend .text--body-2;
      color: $neutral--80;
    }
  }

  .sam-search__input:-webkit-autofill,
  .sam-search__input:-webkit-autofill:hover,
  .sam-search__input:-webkit-autofill:focus,
  .sam-search__input:-webkit-autofill:active,
  .sam-search__input:-webkit-autofill:first-line {
    @extend .text--body-1;
    @extend .shadow--dropdown;

    transition: background-color 5000s ease-in-out 0s !important;
    color: $neutral--90 !important;
    background-color: transparent !important;
  }

  &.is-filter {
    background-color: $primary--white;
    border-color: $neutral--30;
    color: $neutral--60;

    ::-webkit-input-placeholder,
    ::-moz-placeholder,
    :-ms-input-placeholder,
    ::-webkit-input-placeholder,
    ::placeholder {
      color: $neutral--60;
    }

    &:hover:not(.is-disabled) {
      background-color: $primary--white;
      border-color: $informative--hover;
      color: $informative--hover;

      ::-webkit-input-placeholder,
      ::-moz-placeholder,
      :-ms-input-placeholder,
      ::-webkit-input-placeholder,
      ::placeholder {
        color: $informative--hover;
      }
    }

    &.is-focus {
      color: $pure--black;
      border-color: $informative--hover;

      ::-webkit-input-placeholder,
      ::-moz-placeholder,
      :-ms-input-placeholder,
      ::-webkit-input-placeholder,
      ::placeholder {
        color: $pure--black;
      }
    }
  }

  // Disabled state
  &.is-disabled {
    border-color: $neutral--40;
    background-color: $neutral--20;

    .sam-search__input,
    .sam-search__icon--interactive {
      cursor: not-allowed;
      color: $neutral--60;

      &::-webkit-input-placeholder,
      &::-moz-placeholder,
      &:-ms-input-placeholder,
      &::-webkit-input-placeholder,
      &::placeholder {
        color: $neutral--40;
      }
    }
  }
}
</style>
